import { useEffect, useMemo, useState } from "react";
import { useLessonSettings } from "hooks/redux";
import { Space } from "components/Common/Space";
import { SentenceItem } from "../SentenceItem";
import {
  TChooseRightWordExerciseBlank,
  TChooseRightWordFormExercise,
  TExerciseSentence,
} from "types/app/exercises";
import "./SentenceList.styles.scss";
import { is } from "date-fns/locale";

type TSentenceListProps = {
  /**
   * An array of sentence data to construct an exercise.
   */
  sentences: TExerciseSentence<TChooseRightWordExerciseBlank>[];
  /**
   * Current exercise with all sentences.
   */
  exercise: TChooseRightWordFormExercise;
  /**
   * Function to update the exercise on the server.
   */
  onSaveExercise: (updatedExercise: TChooseRightWordFormExercise) => void;
};

export const SentenceList: React.FC<TSentenceListProps> = (props) => {
  const { sentences, exercise, onSaveExercise } = props;

  const [innerSentences, setInnerSentences] =
    useState<TExerciseSentence<TChooseRightWordExerciseBlank>[]>(sentences);

  const { isExercisePreviewMode, isExerciseLiveMode } = useLessonSettings();

  useEffect(() => {
    if (isExercisePreviewMode) {
      setInnerSentences(
        sentences.map((sentence) => ({
          ...sentence,
          chunks: sentence.chunks.map((chunk) => ({
            ...chunk,
            blank: chunk.blank
              ? {
                  ...chunk.blank,
                  currentAnswer:
                    chunk.blank?.answers.find((answer) => answer.isCorrect) ||
                    null,
                  isCorrectAnswer: true,
                  isAllAttemptsFailed: false,
                  isEmpty: false,
                }
              : null,
          })),
        }))
      );
    }
  }, [isExercisePreviewMode, isExerciseLiveMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local state when sentences prop changes (from WebSocket updates)
  useEffect(() => {
    if (!isExercisePreviewMode) {
      setInnerSentences(sentences);
    }
  }, [sentences, isExercisePreviewMode]);

  const isShowResults: boolean = useMemo(
    () =>
      isExercisePreviewMode ||
      innerSentences.every((sentence) =>
        sentence.chunks.every((chunk) => chunk.blank?.currentAnswer !== null)
      ),
    [isExercisePreviewMode, innerSentences]
  );

  const sentenceChangeHandler = (
    updatedSentence: TExerciseSentence<TChooseRightWordExerciseBlank>
  ) => {
    const updatedSentences = innerSentences.map((sentence) =>
      sentence.id === updatedSentence.id ? updatedSentence : sentence
    );

    setInnerSentences(updatedSentences);

    if (
      updatedSentences.every((sentence) =>
        sentence.chunks.every((chunk) => chunk.blank?.currentAnswer !== null)
      )
    ) {
      const updatedExercise = {
        ...exercise,
        payload: {
          ...exercise.payload,
          data: {
            ...exercise.payload.data,
            sentences: updatedSentences,
          },
        },
      };

      onSaveExercise(updatedExercise);
    }
  };

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      {innerSentences.map((sentence) => (
        <SentenceItem
          key={sentence.id}
          sentence={sentence}
          showResult={isShowResults}
          onSentenceChange={sentenceChangeHandler}
        />
      ))}
    </Space>
  );
};
